import {
    Alert,
    Box,
    Button,
    FormField,
    Input,
    Link,
    Modal,
    SpaceBetween,
    TextContent,
    Textarea,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import '../styles/feedback-modal.scss';
import { useTranslate } from 'src/i18n/useTranslate';
import { useApi } from 'src/hooks/useApi';
import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from '@amzn/katal-metrics';
import {
    NOTIFICATIONS,
    useNotifications,
} from './providers/NotificationsProvider';
import { useLocation } from 'react-router-dom';
import { ArticleDetailsApiType } from 'src/types/useApi';

const DRAFT_KEY = 'gru_feedback_draft';

interface FormDraft {
    title: string;
    description: string;
    userAlias: string;
}

const DEFAULT_DRAFT: FormDraft = {
    title: '',
    description: '',
    userAlias: '',
};

interface FeedbackModalProps {
    feedbackModalOpen: boolean;
    setFeedbackModalOpen: (open: boolean) => void;
}

export default function FeedbackModalV2({
    feedbackModalOpen,
    setFeedbackModalOpen,
}: FeedbackModalProps) {
    const t = useTranslate();
    const location = useLocation();
    const { pathname, hash } = location;
    const { publishNotification } = useNotifications();

    const isDocumentPage = /^\/\d+/.test(pathname);
    const artifactId = isDocumentPage ? pathname.slice(1) : undefined;
    const topicId = hash?.split('/')?.[hash?.split('/').length - 2];

    const apiResult: ArticleDetailsApiType = useApi({
        url: `learning-artifacts/${artifactId || ''}`,
        method: 'GET',
    });
    const getArticleDetails = apiResult?.makeRequest ?? (() => {});
    const articleData = apiResult.data;

    const getPageLabel = () => {
        if (pathname === '/') return t('homepage', 'Homepage');
        if (pathname === '/search') return t('search_page', 'Search Page');
        if (pathname === '/favorites')
            return t('favorites_page', 'My Favorites Page');
        if (pathname === '/recent')
            return t('recent_page', 'Recently Viewed Page');
        return articleData?.name || t('none', 'None');
    };

    const findTopicPath = (
        contentObjects: any,
        topicID: string
    ): string | null => {
        let output: string | null = null;
        const search = (topics: any[], path: string): boolean => {
            if (!Array.isArray(topics)) return false;
            for (const topic of topics) {
                if (!topic) continue;
                const p = path ? `${path} : ${topic.name}` : topic.name;
                if (topic.lcmsXmlGUID === topicID) {
                    output = p;
                    return true;
                }
                if (topic.topics?.length && search(topic.topics, p))
                    return true;
            }
            return false;
        };
        if (!contentObjects?.lessons) return null;
        for (const lesson of contentObjects.lessons) {
            if (lesson?.topics && search(lesson.topics, lesson.name)) break;
        }
        return output;
    };

    const artifactTitle = getPageLabel();
    const topicTitle = isDocumentPage
        ? findTopicPath(articleData?.contentObjects, topicId) ||
          hash?.split('/').pop() ||
          t('none', 'None')
        : artifactTitle;

    useEffect(() => {
        if (artifactId) {
            try {
                getArticleDetails({ url: `learning-artifacts/${artifactId}` });
            } catch (err) {
                console.error(err);
            }
        }
    }, [artifactId]);

    const [form, setForm] = useState<FormDraft>(DEFAULT_DRAFT);
    const [ticketUrl, setTicketUrl] = useState<string | null>(null);
    const [hasDraft, setHasDraft] = useState(false);

    const setField = <K extends keyof FormDraft>(key: K, value: FormDraft[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    useEffect(() => {
        if (feedbackModalOpen) {
            const saved = localStorage.getItem(DRAFT_KEY);
            setHasDraft(!!saved && saved !== JSON.stringify(DEFAULT_DRAFT));
        }
    }, [feedbackModalOpen]);

    useEffect(() => {
        if (JSON.stringify(form) !== JSON.stringify(DEFAULT_DRAFT)) {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
        }
    }, [form]);

    const loadDraft = () => {
        const saved = localStorage.getItem(DRAFT_KEY);
        if (saved) {
            setForm(JSON.parse(saved));
            setHasDraft(false);
        }
    };

    const saveFeedbackErrorMetricsPublisher =
        initialMetricsPublisher.newChildActionPublisherForMethod(
            'SaveFeedbackError'
        );

    const {
        makeRequest,
        data: response,
        error,
        loading,
    } = useApi({
        method: 'POST',
        url: '/feedback/v2',
    });

    const canSubmit =
        form.title.trim().length > 0 &&
        form.description.trim().length > 0 &&
        form.userAlias.trim().length > 0;

    const handleDismiss = () => {
        setTicketUrl(null);
        setFeedbackModalOpen(false);
    };

    const handleSubmit = () => {
        if (!canSubmit) return;
        makeRequest({
            data: {
                pageType: isDocumentPage ? 'document' : 'other',
                title: form.title,
                description: form.description,
                userAlias: form.userAlias || undefined,
                pageUrl: window.location.href,
                artifactTitle,
                topicTitle,
                browser: navigator.userAgent.includes('Chrome')
                    ? 'Chrome'
                    : navigator.userAgent.includes('Firefox')
                    ? 'Firefox'
                    : navigator.userAgent.includes('Safari')
                    ? 'Safari'
                    : 'Unknown',
                userAgent: navigator.userAgent,
                osLanguage: navigator.language,
                clientWidth: `${window.innerWidth}px`,
                clientHeight: `${window.innerHeight}px`,
            },
        });
    };

    useEffect(() => {
        if (response?.ticketUrl) {
            setTicketUrl(response.ticketUrl);
            setForm(DEFAULT_DRAFT);
            localStorage.removeItem(DRAFT_KEY);
            setHasDraft(false);
            publishNotification({
                type: 'success',
                content: t(
                    'sim_ticket_success',
                    'Ticket created successfully!'
                ),
                id: NOTIFICATIONS.FEEDBACK_SUCCESS,
            });
        }
    }, [response]);

    useEffect(() => {
        if (error) {
            saveFeedbackErrorMetricsPublisher.publish(
                new KatalMetrics.Metric.String(
                    'gru.error.errorType',
                    'Save Feedback V2'
                )
            );
            publishNotification({
                type: 'error',
                content: t(
                    'feedback_error',
                    'Failed to submit. Please try again later.'
                ),
                id: NOTIFICATIONS.FEEDBACK_ERROR,
            });
        }
    }, [error]);

    return (
        <div data-feedback-modal>
            <Modal
                data-testid="feedback-modal"
                visible={feedbackModalOpen}
                size="large"
                onDismiss={handleDismiss}
                header={<h2>{t('feedback_modal_heading', 'Give Feedback')}</h2>}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={handleDismiss}>
                                {t('cancel_button', 'Cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                loading={loading}
                                disabled={!canSubmit || !!ticketUrl}
                                data-testid="feedback-save-button"
                                onClick={handleSubmit}
                            >
                                {t('submit', 'Submit')}
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <div data-feedback-modal-content-container>
                    <SpaceBetween size="m">
                        {hasDraft && (
                            <Alert
                                type="info"
                                action={
                                    <Button onClick={loadDraft}>
                                        {t('load_draft', 'Load draft')}
                                    </Button>
                                }
                                dismissible
                                onDismiss={() => setHasDraft(false)}
                            >
                                {t(
                                    'draft_available',
                                    'You have a saved draft from a previous session.'
                                )}
                            </Alert>
                        )}

                        {ticketUrl && (
                            <Alert type="success" statusIconAriaLabel="Success">
                                {t('ticket_created', 'Ticket created!')}{' '}
                                <Link href={ticketUrl} external>
                                    {t('view_ticket', 'View ticket')}
                                </Link>
                            </Alert>
                        )}

                        <SpaceBetween size="xs">
                            <TextContent>
                                <small>
                                    <strong>
                                        {t('artifact_title', 'Artifact')}:
                                    </strong>{' '}
                                    {artifactTitle}
                                </small>
                                <br />
                                <small>
                                    <strong>{t('topic', 'Topic')}:</strong>{' '}
                                    {topicTitle}
                                </small>
                            </TextContent>
                        </SpaceBetween>

                        <FormField
                            label={t('summary', 'Summary') + '*'}
                            description={t(
                                'summary_description',
                                'Summarize your feedback in a short sentence or phrase.'
                            )}
                        >
                            <Input
                                value={form.title}
                                onChange={({ detail }) =>
                                    setField('title', detail.value)
                                }
                            />
                        </FormField>

                        <FormField
                            label={t('tell_us_more', 'Tell us more') + '*'}
                            description={t(
                                'details_description',
                                'Describe what you observed, why it matters, and how it impacts your work. Include specific examples.'
                            )}
                        >
                            <Textarea
                                value={form.description}
                                onChange={({ detail }) =>
                                    setField('description', detail.value)
                                }
                                rows={4}
                            />
                        </FormField>

                        <FormField label={t('user_alias', 'Your alias') + '*'}>
                            <Input
                                value={form.userAlias}
                                onChange={({ detail }) =>
                                    setField('userAlias', detail.value)
                                }
                            />
                        </FormField>

                        <SpaceBetween size="xs">
                            <TextContent>
                                <small>
                                    <strong>{t('page_link', 'Page')}:</strong>{' '}
                                    {window.location.href}
                                </small>
                            </TextContent>
                        </SpaceBetween>

                        <TextContent>
                            <small>
                                *:{' '}
                                {t(
                                    'feedback_footer',
                                    'These questions are required'
                                )}
                            </small>
                        </TextContent>
                    </SpaceBetween>
                </div>
            </Modal>
        </div>
    );
}
